import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'

import Home from '@/views/Home.vue'
import Login from '@/views/Login.vue'
import Products from '@/views/Products.vue'
import Solutions from '@/views/Solutions.vue'
import SolutionDetail from '@/views/SolutionDetail.vue'
import Wikis from '@/views/Wikis.vue'
import WikiDetail from '@/views/WikiDetail.vue'
import ProductDetail from '@/views/ProductDetail.vue'
import About from '@/views/About.vue'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  {
    path: '/products',
    name: 'Products',
    component: Products,
    meta: { requiresAuth: false }
  },
  {
    path: '/product/:id',
    name: 'ProductDetail',
    component: ProductDetail,
    meta: { requiresAuth: false }
  },
  {
    path: '/solutions/',
    name: 'Solutions',
    component: Solutions,
    meta: { requiresAuth: false }
  },
  {
    path: '/solution/:id',
    name: 'SolutionDetail',
    component: SolutionDetail,
  },
  {
    path: '/wikis/',
    name: 'Wikis',
    component: Wikis,
  },
  {
    path: '/wiki/:id',
    name: 'WikiDetail',
    component: WikiDetail,
  },
  {
    path: '/about',
    name: 'About',
    component: About,
  },
  {
    path: '/user/dashboard',
    name: 'UserDashboard',
    component: () => import('@/views/user/Dashboard.vue'),
    meta: { requiresAuth: true, 'role': 'user' }
  },
  // ==================== 后台管理（统一框架 AdminLayout） ====================
  // URL 与改造前完全一致；所有页面作为 AdminLayout 的子路由渲染
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    redirect: '/admin/dashboard',
    meta: { requiresAuth: true, role: 'admin' },
    children: [
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('@/views/admin/Dashboard.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '仪表盘' }
      },
      {
        path: 'products',
        name: 'ProductManagement',
        component: () => import('@/views/admin/ProductManagement.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '产品管理' }
      },
      {
        path: 'products/edit/:id?',
        name: 'ProductEdit',
        component: () => import('@/views/admin/ProductEdit.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '产品管理' }
      },
      {
        path: 'solutions',
        name: 'SolutionManagement',
        component: () => import('@/views/admin/SolutionManagement.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '解决方案' }
      },
      {
        path: 'solutions/edit/:id?',
        name: 'SolutionEdit',
        component: () => import('@/views/admin/SolutionEdit.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '解决方案' }
      },
      {
        path: 'wikis',
        name: 'WikiManagement',
        component: () => import('@/views/admin/WikiManagement.vue'),
        meta: { requiresAuth: true, role: 'admin', title: 'Wiki 管理' }
      },
      {
        path: 'wikis/edit/:id?',
        name: 'WikiEdit',
        component: () => import('@/views/admin/WikiEdit.vue'),
        meta: { requiresAuth: true, role: 'admin', title: 'Wiki 管理' }
      },
      {
        path: 'categories',
        name: 'CategoryManagement',
        component: () => import('@/views/admin/ProductCategoryManagement.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '产品分类' }
      },
      {
        path: 'categories/edit/:id?',
        name: 'CategoryEdit',
        component: () => import('@/views/admin/ProductCategoryEdit.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '产品分类' }
      },
      {
        path: 'industries',
        name: 'IndustryManagement',
        component: () => import('@/views/admin/IndustryManagement.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '行业管理' }
      },
      {
        path: 'industries/edit/:id?',
        name: 'IndustryEdit',
        component: () => import('@/views/admin/IndustryEdit.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '行业管理' }
      },
      {
        path: 'navbar',
        name: 'NavbarManagement',
        component: () => import('@/views/admin/NavbarManagement.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '导航栏管理' }
      },
      {
        path: 'register-codes',
        name: 'RegisterCodeManagement',
        component: () => import('@/views/admin/RegisterCodeManagement.vue'),
        meta: { requiresAuth: true, role: 'admin', title: '注册码管理' }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})
// 路由守卫
router.beforeEach((to, from, next) => {
  const userStore = useUserStore()
  // 加载用户信息
  userStore.loadUser()

  // 检查是否需要认证
  if (to.meta.requiresAuth) {
    if (!userStore.isLoggedIn) {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    } else if (to.meta.role && userStore.role !== to.meta.role) {
      // 角色不匹配，跳转到对应角色的首页
      const defaultPath = userStore.role === 'admin' ? '/admin/dashboard' : '/user/dashboard'
      next(defaultPath)
    } else {
      next()
    }
  } else {
    // 如果已登录，访问登录页自动跳转
    if (to.path === '/login' && userStore.isLoggedIn) {
      const defaultPath = userStore.role === 'admin' ? '/admin/dashboard' : '/user/dashboard'
      next(defaultPath)
    } else {
      next()
    }
  }
})

export default router
